import { useContext, useState, useEffect, useRef } from 'react';

import { useVariationIds } from '@thd-olt-component-react/personalization-utils';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useHelmet, LinkTag } from '@thd-nucleus/thd-helmet';

import Helper from '../component/Helper';

import { IMAGE_TYPES } from '../constants';

export const useDecisionEngine = ({ media }) => {
  const { segment } = useContext(ExperienceContext);
  const isAreaRugs = segment === 'area-rugs';
  const campaignId = useConfigService('media-gallery.de-main-image');
  const enableDeFeature = useConfigService('fs:enableDeFeature');

  const [imageType, setImageType] = useState('');
  const [loading, setLoading] = useState(isAreaRugs && enableDeFeature);

  const timeoutRef = useRef();

  const threeHrExpiration = new Date(new Date().setHours(new Date().getHours() + 3)).getTime() - Date.now();

  const variationIdResponse = useVariationIds({
    campaignIds: [{
      campaignId
    }],
    localStorageOpts: {
      expiration: threeHrExpiration
    },
    isReady: !!campaignId && enableDeFeature
  });

  const productMetaDataHandler = () => {
    if (!(enableDeFeature && isAreaRugs)) {
      return [];
    }

    const { image: primaryImage, images = [] } = media || {};
    const primaryImageUrl = primaryImage?.url?.replace(/_\d+./gm, '_600.');
    const tags = [];

    const filteredImages = images?.filter((image) => {
      return IMAGE_TYPES.find((type) => image?.type === type);
    }) || [];

    filteredImages.forEach(({ url, type }) => {
      const imgUrl = url?.replace(/<SIZE>/ig, '600');

      // Skip primary image, as this is already loaded in the head within ProductMetadata.js
      if (imgUrl !== primaryImageUrl) {
        tags.push(
          new LinkTag({
            rel: 'preload',
            href: imgUrl,
            id: `preLoadImg ${type}`,
            as: 'image',
            media: '(min-width: 600px)',
            fetchpriority: 'high'
          }),
        );
      }
    });

    tags.push(
      new LinkTag({
        rel: 'preload',
        href: 'https://assets.thdstatic.com/images/v1/gray-image-placeholder.jpg',
        id: 'placeholderImg',
        as: 'image',
        fetchpriority: 'high'
      }),
    );
    return tags;
  };

  useHelmet('decisionEngine.mediaGallery', {}, productMetaDataHandler, [segment]);

  const decision = variationIdResponse?.variationIds?.[0]?.decision;

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
    }, 10000);

    window.LIFE_CYCLE_EVENT_BUS.on('personalization-utils.timeout', () => {
      setLoading(false);
    });

    window.LIFE_CYCLE_EVENT_BUS.on('personalization-utils.deError', () => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      clearTimeout(timeoutRef.current);
    }
  }, [loading]);

  useEffect(() => {
    const imageTypeFromMap = Helper.armToImageMapping({ decision });
    if (variationIdResponse?.variationIds) {
      setImageType(imageTypeFromMap);
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('media-gallery-rendered', () => {
        setLoading(false);
      });
    }
  }, [variationIdResponse?.variationIds]);

  return { imageType, loading };
};